// PURPOSE CODES — what a payment is FOR (ADR-008 §3).
//
// `fin_invoice` already carries `module` + `ref_type` + `ref_id`. That says
// which ROW a charge points at. It does not say what the money is for, and it
// cannot be rolled up: "ref_type = 'fine'" is not an accounting category, and no
// report can ask it "how much did we take in deposits this year".
//
// THE OLD-ERP ANSWER is a central `fee_head_master` table that one team owns and
// every module files a ticket against. This repo already has a better pattern and
// it is proven: permissions are declared per module in its `schemas` lane and
// unioned at the boundary by `mergeGrants`, so `modules/fin` is never edited to
// add a library capability. Purpose codes take exactly that shape — which is why
// the TYPE lives here, in a package both ends already depend on, rather than in
// `modules/fin` where a library import would be the module → module edge Fig 4.1
// bans.
//
// THREE LAWS make this enterprise-grade rather than decorative:
//
//   · `kind` IS THE LOAD-BEARING FIELD. It is what keeps a refundable deposit
//     out of revenue aggregates. Misclassifying here is an accounting error, not
//     a reporting nit.
//   · ADDITIVE-ONLY, FOREVER. Historical rows reference these codes; renaming or
//     retiring one orphans every report that spans the change.
//   · THE CODE IS FROZEN ONTO THE ROW at creation, never resolved through a live
//     lookup. A report that re-derives purpose from the current registry
//     rewrites history when a label changes. Store the code; treat the registry
//     as display. (The same law ADR-009 §2 gives feature maps and ADR-006 gives
//     template slugs.)
import { z } from "zod";

/**
 * What the money IS, for accounting.
 *
 *   revenue        — the institution earned it and keeps it.
 *   penalty        — earned, but a fine rather than a sale. Split out because
 *                    "how much of our income is fines?" is a question an
 *                    institution has to be able to answer about itself.
 *   liability      — held on someone else's behalf and owed back. A caution
 *                    deposit is the case this exists for: counting it as revenue
 *                    overstates income and understates what is owed.
 *   deposit_refund — money going OUT to discharge a liability. Negative to the
 *                    institution, and never a negative "sale".
 */
export const PURPOSE_KINDS = ["revenue", "penalty", "liability", "deposit_refund"] as const;
export const PurposeKind = z.enum(PURPOSE_KINDS);
export type PurposeKind = z.infer<typeof PurposeKind>;

export interface PurposeSpec {
  readonly label: string;
  readonly kind: PurposeKind;
  /** Optional prose for a finance screen — never parsed, never keyed on. */
  readonly note?: string;
}

/**
 * A module's own registry. Declared `as const satisfies PurposeCatalog` so a
 * typo in a key is a compile error at every call site and the whole set stays
 * greppable.
 */
export type PurposeCatalog = Record<string, PurposeSpec>;

/**
 * The code as stored. DELIBERATELY A PLAIN STRING at the persistence boundary,
 * not a union of the codes this build happens to know.
 *
 * A row written last year may name a code a later build renamed away — which the
 * additive-only law forbids, but a database outlives a law — and a row written by
 * a NEWER build names a code this one has never heard of. Either way the honest
 * behaviour is to carry the string and let the registry lookup return undefined,
 * not to fail the read. Validation happens where a code is CHOSEN, against the
 * merged catalog; see `isKnownPurpose`.
 */
export const PurposeCode = z
  .string()
  .trim()
  .min(3)
  .max(64)
  .regex(/^[a-z][a-z0-9]*(\.[a-z0-9]+)+$/, "Use dotted lowercase, e.g. lib.membership.new");
export type PurposeCode = z.infer<typeof PurposeCode>;

/**
 * Key-wise union of module catalogs, called ONCE at the gateway.
 *
 * A DUPLICATE KEY THROWS rather than last-one-wins. Two modules claiming
 * `lib.fine.overdue` with different kinds is not a merge conflict to resolve
 * quietly — it is two teams disagreeing about whether money is revenue or a
 * liability, and the loud failure is at boot, where somebody can fix it, rather
 * than in a year-end report nobody re-reads. `mergeGrants` unions instead,
 * correctly: two modules granting the same capability agree by construction,
 * while two modules classifying the same money do not.
 *
 * Null-prototype accumulator: a crafted `__proto__` key must become an own
 * property, never a prototype write.
 */
export function mergePurposes(...catalogs: readonly PurposeCatalog[]): PurposeCatalog {
  const out: Record<string, PurposeSpec> = Object.create(null);
  for (const catalog of catalogs) {
    for (const key of Object.keys(catalog)) {
      const spec = catalog[key] as PurposeSpec;
      const existing = Object.hasOwn(out, key) ? out[key] : undefined;
      if (existing && existing.kind !== spec.kind) {
        throw new Error(
          `purpose code "${key}" is declared twice with different kinds ("${existing.kind}" and "${spec.kind}")`,
        );
      }
      out[key] = spec;
    }
  }
  return out;
}

/** Is this code one the running build knows? The gate on CHOOSING a code. */
export const isKnownPurpose = (catalog: PurposeCatalog, code: string): boolean => Object.hasOwn(catalog, code);

/**
 * The spec for a stored code, or undefined.
 *
 * Undefined is a normal answer, not an error: see `PurposeCode`. Callers render
 * the raw code when the registry does not know it, which is strictly more useful
 * than hiding a row whose money is perfectly real.
 */
export const purposeMeta = (catalog: PurposeCatalog, code: string): PurposeSpec | undefined =>
  Object.hasOwn(catalog, code) ? catalog[code] : undefined;

/** A label for display, falling back to the raw code. */
export const purposeLabel = (catalog: PurposeCatalog, code: string): string =>
  purposeMeta(catalog, code)?.label ?? code;

/**
 * Does this code count toward REVENUE?
 *
 * The one question the aggregates must get right, and the reason `kind` exists.
 * An UNKNOWN code answers false — a code this build cannot classify must not be
 * silently counted as income, and under-reporting revenue is a discrepancy
 * somebody investigates while over-reporting is one they act on.
 */
export const isRevenue = (catalog: PurposeCatalog, code: string): boolean => {
  const kind = purposeMeta(catalog, code)?.kind;
  return kind === "revenue" || kind === "penalty";
};
